/**
 * @TODO This whole thing and its composition has to be refactored at some point...
 */
import type {
  SearchParams,
  TravelType,
} from '@/modules/common/context/QueryParams/models';
import type {Hotel} from '@/redux/v1/hotels/list/models';

import {css} from '@emotion/react';
import {
  Button,
  CashbackInterceptor,
  CashbackInterceptorSize,
  DiscountInterceptor,
  DiscountInterceptorSize,
  Flex,
  HotelDetailsOverlay,
  Notice,
  PictureFallback,
  Skeleton,
} from '@travel-frontend/ui-components';
import {memo, useCallback, useRef, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {
  Link,
  type NavigateOptions,
  type To,
  useHref,
  useNavigate,
} from 'react-router';

import {HotelBox as HotelBoxUI} from '@/components/HotelBox';
import {HotelBoxGallery} from '@/components/HotelBoxGallery';
import {PositionableNonBlocking} from '@/components/HotelBoxGallery/components/PositionableNonBlocking';
import {HotelBoxPlaceholder} from '@/components/HotelBoxPlaceholder';
import {HotelOfferInfo} from '@/components/HotelOfferInfo';
import {HotelSummary} from '@/components/HotelSummary';
import {HotelTypeBadge} from '@/components/HotelTypeBadge';
import {PriceTag} from '@/components/PriceTag';
import {ShowMoreButton} from '@/components/ShowMoreButton';
import {SponsoredBadge} from '@/components/SponsoredBadge';
import {config} from '@/config';
import {
  HotelPictureGalleryOverlay,
  type Props as HotelPictureGalleryOverlayProps,
} from '@/modules/common/components/HotelPictureGalleryOverlay';
import {RatingSummary} from '@/modules/common/components/RatingSummary';
import {
  useBoardType,
  useCombinedCampaign,
  useHotelDetails,
  useLocalSplit,
  useSimplifiedRoomString,
} from '@/modules/common/hooks';
import {useTourOperatorDiscountCampaign} from '@/modules/common/hooks/useCampaigns/useTourOperatorDiscountCampaign';
import {useEnvironment} from '@/modules/common/hooks/useEnvironment';
import {
  type Section as HotelDetailsOverlaySection,
  useHotelDetailsOverlay,
} from '@/modules/common/hooks/useHotelDetailsOverlay';
import {useSponsoredAdIntersectionTracking} from '@/modules/common/hooks/useSponsoredAdIntersectionTracking';
import {useTracking} from '@/modules/common/hooks/useTracking';
import {createP195Classname} from '@/modules/common/utils/createP195Classname';
import {Favorite} from '@/modules/hotels/components/List/components/HotelBox/components/Favorite';
import {PromotionBadges} from '@/modules/hotels/components/List/components/HotelBox/components/PromotionBadges';
import {getRatingData} from '@/modules/hotels/utils/getRatingData';
import {mapHotelDataToEcommerceItem} from '@/redux/middleware/googleTagManager/utils/hotelData';
import {testId} from '@/utils/testId';

export type Props = {
  hotelId: string;
  link: {to: To; options: NavigateOptions};
  offer: Hotel | null;
  position: number;
  rooms: SearchParams['rooms'];
  travelType: TravelType;
  small?: boolean;
  onSelect?: ({
    hotelId,
    origin,
    position,
  }: {
    hotelId: string;
    origin: 'hotelbox' | 'pictureGalleryOverlay';
    position: number;
  }) => void;
};

const TRACKING_TEXT_BY_SECTION_NAME: Record<
  HotelDetailsOverlaySection,
  string
> = {
  rating: 'rating',
  amadeusRating: 'rating',
  attributes: 'facilities',
  description: 'description',
  map: 'region',
};

export const HotelBox = memo(function MemoizedHotelBox({
  hotelId,
  link,
  offer,
  position,
  rooms,
  travelType,
  onSelect,
  small,
}: Props) {
  const {t} = useTranslation();
  const {isDesktop} = useEnvironment();
  const href = useHref(link.to);
  /**
   * @TODO
   * Move this out of here and make it one (stable) `campaign`-prop (with a `type`),
   * OR make the existing hooks not depend on router-state.
   * The goal is to make the HotelBox properly memoize when router-state updates
   * but doesn't actually "change" (#hash navigation for overlay state).
   */
  const {getAssociatedDiscountCampaign} = useTourOperatorDiscountCampaign();
  const {shouldShowBanner: shouldShowCombinedBanner, shouldShowInterceptor} =
    useCombinedCampaign();
  // end TODO
  const navigate = useNavigate();

  const priceCacheEnabled =
    useLocalSplit('FBO_2956_price_cache').treatment === 'on';
  const favoriteEnabled =
    useLocalSplit('FPO_6430_wishlist_feature_toggle').treatment === 'on';

  const [
    hotelPictureGalleryOverlayVisible,
    setHotelPictureGalleryOverlayVisible,
  ] = useState(false);
  const [hotelPictureGalleryOverlayView, setHotelPictureGalleryOverlayView] =
    useState<HotelPictureGalleryOverlayProps['view']>();
  const [
    hotelPictureGalleryOverlayImages,
    setHotelPictureGalleryOverlayImages,
  ] = useState<HotelPictureGalleryOverlayProps['hotelImages']>();

  const [
    hotelDetails,
    {hotelDetails: hotelDetailsStatus, hotelImages: hotelImagesStatus},
  ] = useHotelDetails(hotelId);
  const tileRef = useRef<HTMLDivElement>(null);
  const {dispatchGenericTrackingEvent} = useTracking();

  useSponsoredAdIntersectionTracking(tileRef.current, {
    hotelId,
    position,
    viewableImpressionUrl: offer?.sponsored?.viewableImpressionUrl,
  });

  const rating = getRatingData({
    hotelOffer: offer,
  });

  const hotelDetailsOverlay = useHotelDetailsOverlay({
    hotelId,
    amadeusTrustedReviewsAvailable: rating != null,
    onClickReviewImage: ({review}) => {
      setHotelPictureGalleryOverlayVisible(true);
      setHotelPictureGalleryOverlayImages(
        review.photos.map((photo) => ({
          image_url: photo,
          categoryCode: [],
          provider: t('imageGalleryLayer:customer'),
        }))
      );
    },
  });
  const hotelDetailsEnabled = hotelDetailsOverlay.data !== null;

  const shouldShowChildrenAge = Boolean(rooms?.childrenCount);

  const infoTripRooms = config.hotelDetails.roomTypeHidden
    ? []
    : (offer?.rooms ?? []);

  const roomType = useSimplifiedRoomString(infoTripRooms);
  const {boardType} = useBoardType(infoTripRooms);

  const handleHotelPictureGalleryOverlayClose = () => {
    setHotelPictureGalleryOverlayVisible(false);
  };

  const handleHotelPictureGalleryOverlayOpen = useCallback(() => {
    setHotelPictureGalleryOverlayVisible(true);
    setHotelPictureGalleryOverlayImages(
      hotelDetails?.images.map((image) => ({
        image_url: image.large.url,
        categoryCode: image.categoryCodes,
      }))
    );
    dispatchGenericTrackingEvent({
      event: 'gallery',
      event_action: 'gallery_details_click',
      non_interaction: false,
    });
  }, [dispatchGenericTrackingEvent, hotelDetails?.images]);

  const handleHotelPictureGalleryOverlayCTAButtonClick = () => {
    if (onSelect) {
      onSelect({hotelId, origin: 'pictureGalleryOverlay', position});
    }
    setHotelPictureGalleryOverlayVisible(false);
    // @FIXME
    // Ideally it would be possible to just make the cta button in the HotelPictureGalleryOverlay a link with the same props as the HotelBox CTA...
    // The composition of the underlying ImageGalleryLayer has to be refactored at some point...
    if (isDesktop) {
      window.open(href, '_blank');
    } else {
      navigate(link.to, link.options);
    }
  };

  const handleFavoriteAddSuccess = useCallback(() => {
    if (!offer) return;

    dispatchGenericTrackingEvent([
      {ecommerce: null},
      {
        event: 'add_to_wishlist',
        event_action: 'hotel_added_to_wishlist',
        non_interaction: false,
        ecommerce: {
          currency: offer.totalPriceDisplay.currency,
          value: offer.totalPriceDisplay.amount / 100,
          items: [
            mapHotelDataToEcommerceItem(
              {
                id: offer.id,
                price: offer?.totalPrice,
                position,
                specials: offer.tourOperatorSpecials,
                ratingScore: offer.rating?.rating ?? null,
                data: hotelDetails,
              },
              '-',
              hotelDetails,
              false,
              true
            ),
          ],
        },
      },
    ]);
  }, [dispatchGenericTrackingEvent, hotelDetails, offer, position]);

  if (hotelDetailsStatus !== 'success' || hotelDetails === undefined) {
    return <HotelBoxPlaceholder small={small} />;
  }

  const {name, address, images, category, categoryType, sustainableCertifier} =
    hotelDetails;

  const linkProps =
    offer === null
      ? undefined
      : {
          as: Link,
          to: link.to,
          state: link.options.state,
          target: isDesktop ? '_blank' : '_self',
          onClick: (event: React.MouseEvent<HTMLElement>) => {
            const didClickOnInteractionTriggerInsideContainer =
              tileRef.current?.contains(
                // @ts-expect-error ...
                event.target.closest('[data-interaction-trigger]')
              ) ?? false;

            if (didClickOnInteractionTriggerInsideContainer) {
              event.preventDefault();
              return;
            }
            hotelDetailsOverlay.close(); // only relavant when clicking cta in overlay (noop otherwise)
            if (onSelect) {
              onSelect({hotelId, origin: 'hotelbox', position});
            }
          },
        };

  const campaign = getAssociatedDiscountCampaign(offer);

  const pictureFallback =
    hotelImagesStatus === 'loading' ? (
      <Skeleton variant="rect" height="100%" width="100%" />
    ) : (
      <PictureFallback />
    );

  const promotionBadgesContent = config.hotelDetails
    .promotionBadgesHidden ? null : (
    <PromotionBadges promotions={offer?.tourOperatorSpecials} />
  );

  let interceptorElement = null;
  if (campaign) {
    interceptorElement =
      shouldShowCombinedBanner || shouldShowInterceptor ? (
        <PositionableNonBlocking position="topLeft">
          <CashbackInterceptor
            margin="16px"
            text={campaign.interceptorContent.text}
            highlight={campaign.interceptorContent.highlight}
            size={CashbackInterceptorSize.SMALL}
          />
        </PositionableNonBlocking>
      ) : (
        <PositionableNonBlocking position="topLeft">
          <DiscountInterceptor
            margin="16px"
            text={campaign.interceptorContent.text}
            highlight={campaign.interceptorContent.highlight}
            size={DiscountInterceptorSize.SMALL}
          />
        </PositionableNonBlocking>
      );
  }

  const imageLoadingStrategy = position > 3 ? 'lazy' : 'eager';

  const hotelTypeAttributes = hotelDetails.attributes
    ?.filter((group) => group.name === 'type' || group.name === 'IT03-TYPE')
    .flatMap((group) => group.attributes);

  const summaryElement = (
    <HotelSummary
      sustainableCertifier={sustainableCertifier}
      category={category ?? 0}
      categoryType={categoryType}
      name={name}
      city={address.city}
      region={address.region}
      country={address.country}
      onRegionClick={
        hotelDetailsEnabled
          ? () => {
              hotelDetailsOverlay.changeSection('map');
              hotelDetailsOverlay.open();

              dispatchGenericTrackingEvent({
                event: 'click',
                event_action: 'details_info_click',
                click_text: 'hotel region',
                non_interaction: false,
              });
            }
          : undefined
      }
      linkProps={linkProps}
    />
  );

  const favoriteElement = favoriteEnabled ? (
    <Favorite
      key={hotelId}
      hotelId={hotelId}
      onAddSuccess={handleFavoriteAddSuccess}
    />
  ) : null;

  const hotelBoxElement = (
    <HotelBoxUI
      ref={tileRef}
      data-testid={testId('hotelbox')}
      data-hotel-id={hotelId}
      small={small}
      offerInfoElement={
        <HotelOfferInfo
          stays={offer?.travelDateInfo.stays}
          boardType={boardType}
          roomType={roomType}
          adultCount={rooms?.adultsCount as number}
          childrenAges={rooms?.childrenAges ?? []}
          travelType={travelType}
          small={small}
          shouldShowChildrenAge={shouldShowChildrenAge}
        />
      }
      ratingElement={
        rating != null && (
          <RatingSummary
            size="small"
            ratingScore={rating.score}
            reviewCount={rating.reviewCount}
            recommendationRate={rating.recommendationRate}
            onClickReviewCount={() => {
              hotelDetailsOverlay.changeSection('amadeusRating');
              hotelDetailsOverlay.open();

              dispatchGenericTrackingEvent({
                event: 'click',
                event_action: 'details_info_click',
                click_text: 'hotel rating',
                non_interaction: false,
              });
            }}
          />
        )
      }
      summaryElement={summaryElement}
      favoriteElement={favoriteElement}
      badgesElement={
        <Flex
          spacing="8px"
          direction="row"
          flexWrap="wrap"
          css={css`
            :empty {
              display: none;
            }
          `}
        >
          {hotelTypeAttributes &&
            hotelTypeAttributes.length > 0 &&
            hotelTypeAttributes[0] && (
              <HotelTypeBadge
                type={hotelTypeAttributes[0].name}
                name={hotelTypeAttributes[0].translation}
              />
            )}
          {promotionBadgesContent}
        </Flex>
      }
      galleryElement={
        <HotelBoxGallery
          images={images?.map((image) => image.small.url)}
          fallbackImage={pictureFallback}
          interceptorElement={interceptorElement}
          data-interaction-trigger
          small={small}
          onClickImage={handleHotelPictureGalleryOverlayOpen}
          imageLoadingStrategy={imageLoadingStrategy}
        />
      }
      hotelDetailsElement={
        hotelDetailsEnabled && (
          <ShowMoreButton
            data-interaction-trigger
            onClick={() => {
              hotelDetailsOverlay.changeSection('description');
              hotelDetailsOverlay.open();

              dispatchGenericTrackingEvent({
                event: 'click',
                event_action: 'details_info_click',
                click_text: 'hotel details',
                non_interaction: false,
              });
            }}
          >
            {t('hotels:box.detailsButton')}
          </ShowMoreButton>
        )
      }
      buttonElement={
        <Button
          data-testid={testId('hotelbox-cta')}
          disabled={offer === null}
          fullWidth
          style={{
            // Default in component-library is "max-content".
            // This is problematic however, because we want to let the parent control the width.
            width: '100%',
          }}
          {...linkProps}
        >
          {t('hotels:box.offerButton')}
        </Button>
      }
      priceElement={
        offer !== null && (
          <PriceTag
            {...linkProps}
            totalPrice={offer.totalPriceDisplay}
            totalPriceInternal={offer.totalPrice}
            totalPriceOriginal={offer.totalPriceOriginalDisplay}
            totalDiscountHint={offer.totalDiscountHint}
            totalDiscount={offer.totalDiscountDisplay}
            totalDiscountOriginal={offer.totalDiscount}
            discountType={offer.discountType}
            discountPercentage={offer.discountPercentage}
            canShowPriceCache={priceCacheEnabled}
            pricePerPerson={{
              amount: offer.totalPriceDisplay.amount / rooms.travellerCount,
              currency: offer.totalPriceDisplay.currency,
            }}
            travellerCount={rooms.travellerCount}
            showTotalPrice={!(config.global.pricePerPerson && rooms.length > 1)}
            size="large"
          />
        )
      }
      messageElement={
        offer === null && (
          <Notice variant="error">
            <Trans
              ns="hotels"
              i18nKey="box.noOfferMessage"
              components={{
                b: <b />,
              }}
            />
          </Notice>
        )
      }
    />
  );

  return (
    <div className={createP195Classname(offer)}>
      {offer?.sponsored && (
        <SponsoredBadge color="contentSecondary">
          {t('general:sponsoredAd')}
        </SponsoredBadge>
      )}
      {hotelBoxElement}
      {hotelDetailsEnabled && (
        <HotelDetailsOverlay
          open={hotelDetailsOverlay.isOpen}
          // @ts-expect-error ...
          data={hotelDetailsOverlay.data}
          config={hotelDetailsOverlay.config}
          activeSection={hotelDetailsOverlay.activeSection}
          controls={
            <>
              <Button
                category="secondary"
                onClick={() => {
                  hotelDetailsOverlay.close();
                }}
              >
                {t('hotelDetailsOverlay.buttons.back')}
              </Button>
              <Button {...(offer === null ? {disabled: true} : linkProps)}>
                <span>
                  {offer === null
                    ? t('hotelDetailsOverlay.buttons.noOffers')
                    : t('hotelDetailsOverlay.buttons.offers', {
                        price: config.global.pricePerPerson
                          ? offer.totalPriceDisplay.amount /
                            rooms.travellerCount
                          : offer.totalPriceDisplay.amount,
                        currency: offer.totalPriceDisplay.currency,
                      })}
                </span>
              </Button>
            </>
          }
          onChangeSection={(section) => {
            hotelDetailsOverlay.changeSection(
              section as HotelDetailsOverlaySection
            );

            dispatchGenericTrackingEvent({
              event: 'click',
              event_action: 'details_info_click',
              click_text: TRACKING_TEXT_BY_SECTION_NAME[section],
              non_interaction: false,
            });
          }}
          onClose={() => {
            hotelDetailsOverlay.close();

            dispatchGenericTrackingEvent({
              event: 'click',
              event_action: 'details_info_closed',
              click_text:
                TRACKING_TEXT_BY_SECTION_NAME[
                  hotelDetailsOverlay.activeSection
                ],
              non_interaction: false,
            });
          }}
        />
      )}
      {images.length > 0 && (
        <HotelPictureGalleryOverlay
          hotelDetails={hotelDetails}
          hotelImages={hotelPictureGalleryOverlayImages ?? []}
          isVisible={hotelPictureGalleryOverlayVisible}
          view={hotelPictureGalleryOverlayView}
          offerPrice={offer?.totalPriceDisplay}
          onClose={handleHotelPictureGalleryOverlayClose}
          onClickCTAButton={handleHotelPictureGalleryOverlayCTAButtonClick}
          onChangeView={setHotelPictureGalleryOverlayView}
        />
      )}
    </div>
  );
});
